/**
 * ContainerDetailPage — single container view with status-conditional
 * lifecycle actions (publish / close-joins / seal / milestone / cancel)
 * + participant management (approve / reject / confirm-warehouse) +
 * events timeline + KYC docs.
 *
 * Mirrors Flutter `containers/container_detail_page.dart`. Fields named
 * 1:1 with the server payload (no aliases).
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Container, Loader, Hash, Globe, ArrowLeftRight,
  Calendar, Send, Lock, Anchor, X, CheckCircle2, XCircle, Plus,
  Users, FileText, Truck,
} from 'lucide-react';
import {
  useContainer, useContainerParticipants, useContainerEvents, useContainerDocuments,
  usePublishContainer, useCloseContainerJoins, useSealContainer,
  useAddMilestone, useCancelContainer,
  useApproveParticipant, useRejectParticipant,
} from '@/hooks/useContainers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import { ConfirmDialog, PromptDialog } from '@/components/common/Dialog';
import {
  containerStatusDisplay, participantStatusDisplay,
  containerFillFraction, containerPriceLabel,
} from '@/utils/containerStatus';
import type { ContainerParticipant, MilestoneType } from '@/api/containers';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

const MILESTONE_OPTIONS: { value: MilestoneType; label: string }[] = [
  { value: 'depart',           label: 'إقلاع من المنشأ' },
  { value: 'arrive_port',      label: 'وصول ميناء الوجهة' },
  { value: 'customs_filed',    label: 'تقديم تخليص جمركي' },
  { value: 'customs_duty',     label: 'تحديد الرسوم الجمركية' },
  { value: 'customs_hold',     label: 'احتجاز جمركي' },
  { value: 'customs_released', label: 'إطلاق جمركي' },
  { value: 'customs_seized',   label: 'مصادرة' },
  { value: 'loss_declared',    label: 'الإعلان عن فقد' },
];

export default function ContainerDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const containerId = id ? Number(id) : null;
  const { data: container, isLoading } = useContainer(containerId);
  const participants = useContainerParticipants(containerId);
  const events = useContainerEvents(containerId);
  const documents = useContainerDocuments(containerId);

  const publish = usePublishContainer();
  const closeJoins = useCloseContainerJoins();
  const seal = useSealContainer();
  const addMilestone = useAddMilestone();
  const cancel = useCancelContainer();

  const [milestonePicker, setMilestonePicker] = useState<MilestoneType>('depart');
  const [dialog, setDialog] = useState<
    | { kind: 'publish' | 'close-joins' | 'cancel-container' }
    | { kind: 'seal' }
    | null
  >(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [sealForm, setSealForm] = useState({ sealNumber: '', bookingRef: '', bolNumber: '' });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!container) {
    return (
      <AppLayout>
        <p className="text-center text-text-secondary py-16">
          {t('container_not_found', 'الحاوية غير موجودة.')}
        </p>
      </AppLayout>
    );
  }

  const sd = containerStatusDisplay(container.status);
  const fill = containerFillFraction(container);
  const priceLabel = containerPriceLabel(container);

  const canPublish = container.status === 'draft';
  const canCloseJoins = container.status === 'open_for_joins';
  const canSeal = container.status === 'consolidating' || container.status === 'joins_closed';
  const canMilestone = (['sealed', 'in_transit', 'at_destination_port', 'customs_filed', 'customs_cleared', 'unloading'] as const)
    .includes(container.status as never);
  const canCancel = !['completed', 'cancelled', 'lost', 'seized', 'delivered'].includes(container.status);

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-surface border border-border rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Container size={20} className="text-primary" />
                <span className="font-bold text-lg text-text-primary" dir="ltr">{container.public_code}</span>
                {container.is_tentative && (
                  <span className="text-xs px-2 py-0.5 bg-warning/10 text-warning rounded font-bold">
                    {t('tentative', 'مبدئية')}
                  </span>
                )}
              </div>
              <h1 className="text-xl font-bold text-text-primary mb-1">{container.title}</h1>
              {container.description && (
                <p className="text-sm text-text-secondary">{container.description}</p>
              )}
              <div className="flex items-center gap-1 text-sm text-text-secondary mt-2">
                <Globe size={14} />
                <span dir="ltr">{container.origin_country?.toUpperCase()}</span>
                {container.origin_port_code && <span dir="ltr"> ({container.origin_port_code})</span>}
                <ArrowLeftRight size={14} />
                <span dir="ltr">{container.destination_country?.toUpperCase()}</span>
                {container.destination_port_code && <span dir="ltr"> ({container.destination_port_code})</span>}
              </div>
            </div>
            <span className={`text-xs px-3 py-1.5 rounded-full font-bold ${sd.color}`}>{sd.label}</span>
          </div>

          {/* Capacity bar */}
          {(container.max_weight_kg != null || container.max_cbm != null) && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-text-secondary mb-1">
                <span>
                  {t('container_filled', 'الممتلئ')}: <strong>
                    {container.used_weight_kg != null && container.max_weight_kg != null
                      ? `${Math.round(container.used_weight_kg).toLocaleString()} / ${Math.round(container.max_weight_kg).toLocaleString()} kg`
                      : ''}
                    {container.used_cbm != null && container.max_cbm != null
                      ? ` · ${container.used_cbm.toFixed(2)} / ${container.max_cbm.toFixed(2)} m³`
                      : ''}
                  </strong>
                </span>
                <span>{Math.round(fill * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-border/40 rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${fill * 100}%` }} />
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 text-xs text-text-tertiary mt-3 pt-3 border-t border-border/50">
            {priceLabel && <span>{t('rate', 'السعر')}: <strong className="text-primary">{priceLabel}</strong></span>}
            {container.estimated_departure_at && (
              <span className="flex items-center gap-1">
                <Calendar size={11} />
                {t('departure', 'الإقلاع')}: {new Date(container.estimated_departure_at).toLocaleDateString('ar-SY')}
              </span>
            )}
            {container.estimated_arrival_at && (
              <span className="flex items-center gap-1">
                <Calendar size={11} />
                {t('arrival', 'الوصول')}: {new Date(container.estimated_arrival_at).toLocaleDateString('ar-SY')}
              </span>
            )}
            {container.warehouse_cutoff_at && (
              <span className="flex items-center gap-1">
                <Calendar size={11} />
                {t('cutoff', 'إغلاق المستودع')}: {new Date(container.warehouse_cutoff_at).toLocaleDateString('ar-SY')}
              </span>
            )}
          </div>
        </div>

        {/* Lifecycle Actions */}
        {(canPublish || canCloseJoins || canSeal || canMilestone || canCancel) && (
          <div className="bg-surface border border-border rounded-2xl p-5">
            <h2 className="text-sm font-bold text-text-secondary mb-3">
              {t('lifecycle_actions', 'إجراءات دورة الحياة')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {canPublish && (
                <Button variant="primary" loading={publish.isPending}
                  onClick={() => { setDialogError(null); setDialog({ kind: 'publish' }); }}>
                  <Send size={16} className="ml-1" /> {t('publish', 'نشر')}
                </Button>
              )}
              {canCloseJoins && (
                <Button variant="secondary" loading={closeJoins.isPending}
                  onClick={() => { setDialogError(null); setDialog({ kind: 'close-joins' }); }}>
                  <Lock size={16} className="ml-1" /> {t('close_joins', 'إغلاق التسجيل')}
                </Button>
              )}
              {canSeal && (
                <Button variant="primary"
                  onClick={() => { setDialogError(null); setSealForm({ sealNumber: '', bookingRef: '', bolNumber: '' }); setDialog({ kind: 'seal' }); }}>
                  <Anchor size={16} className="ml-1" /> {t('seal_container', 'ختم الحاوية')}
                </Button>
              )}
              {canMilestone && (
                <MilestoneAction
                  pick={milestonePicker} setPick={setMilestonePicker}
                  loading={addMilestone.isPending}
                  onAdd={() => addMilestone.mutate({ id: containerId!, type: milestonePicker })}
                />
              )}
              {canCancel && (
                <Button variant="ghost" loading={cancel.isPending}
                  onClick={() => { setDialogError(null); setDialog({ kind: 'cancel-container' }); }}>
                  <X size={16} className="ml-1" /> {t('cancel_container', 'إلغاء الحاوية')}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="bg-surface border border-border rounded-2xl p-5">
          <h2 className="text-sm font-bold text-text-secondary mb-3 flex items-center gap-2">
            <Users size={16} />
            {t('participants', 'المشاركون')}
            {participants.data && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {participants.data.length}
              </span>
            )}
          </h2>
          {participants.isLoading ? (
            <div className="flex justify-center py-6 text-text-tertiary">
              <Loader size={20} className="animate-spin" />
            </div>
          ) : !participants.data || participants.data.length === 0 ? (
            <p className="text-sm text-text-tertiary text-center py-6">
              {t('no_participants_yet', 'لا مشاركين بعد. انشر الحاوية لتظهر للمرسلين.')}
            </p>
          ) : (
            <div className="space-y-2">
              {participants.data.map((p) => (
                <ParticipantRow key={p.id} participant={p} containerId={containerId!} />
              ))}
            </div>
          )}
        </div>

        {/* Documents */}
        {documents.data && documents.data.length > 0 && (
          <div className="bg-surface border border-border rounded-2xl p-5">
            <h2 className="text-sm font-bold text-text-secondary mb-3 flex items-center gap-2">
              <FileText size={16} /> {t('documents', 'الوثائق')} ({documents.data.length})
            </h2>
            <div className="space-y-1.5">
              {documents.data.map((d) => (
                <a key={d.id} href={d.file_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline">
                  <FileText size={14} />
                  <span>{d.file_name || d.kind}</span>
                  <span className="text-xs text-text-tertiary">
                    ({new Date(d.uploaded_at).toLocaleDateString('ar-SY')})
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Events Timeline */}
        {events.data && events.data.length > 0 && (
          <div className="bg-surface border border-border rounded-2xl p-5">
            <h2 className="text-sm font-bold text-text-secondary mb-3">
              {t('container_events', 'الأحداث')}
            </h2>
            <div className="space-y-2">
              {events.data.map((e) => (
                <div key={e.id} className="flex items-start gap-3 text-sm">
                  <div className="bg-primary/10 text-primary rounded-full p-1.5 mt-0.5">
                    <Truck size={12} />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-text-primary">{e.type}</div>
                    {e.location_text && (
                      <div className="text-xs text-text-tertiary">{e.location_text}</div>
                    )}
                    {e.note && <div className="text-xs text-text-secondary mt-0.5">{e.note}</div>}
                    <div className="text-xs text-text-tertiary mt-0.5">
                      {new Date(e.occurred_at).toLocaleString('ar-SY')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lifecycle dialogs */}
        <ConfirmDialog
          open={dialog?.kind === 'publish'}
          title={t('publish_container_title', 'نشر الحاوية')}
          body={t('publish_confirm', 'ستصبح الحاوية مرئية للمرسلين. تأكيد؟')}
          loading={publish.isPending}
          errorMessage={dialogError}
          confirmLabel={t('publish', 'نشر')}
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            try { await publish.mutateAsync(containerId!); setDialog(null); }
            catch (e: any) { setDialogError(e?.message || 'فشل النشر'); }
          }}
        />
        <ConfirmDialog
          open={dialog?.kind === 'close-joins'}
          title={t('close_joins_title', 'إغلاق التسجيل')}
          body={t('close_joins_confirm', 'لا يستطيع مرسلون جدد الانضمام بعد ذلك. لا يمكن التراجع.')}
          loading={closeJoins.isPending}
          errorMessage={dialogError}
          confirmLabel={t('close_joins', 'إغلاق التسجيل')}
          destructive
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            try { await closeJoins.mutateAsync(containerId!); setDialog(null); }
            catch (e: any) { setDialogError(e?.message || 'فشل الإغلاق'); }
          }}
        />
        <PromptDialog
          open={dialog?.kind === 'cancel-container'}
          title={t('cancel_container', 'إلغاء الحاوية')}
          body={t('cancel_container_body', 'تأكيد الإلغاء؟ كل المشاركين سيُخطَرون.')}
          inputLabel={t('cancel_reason_optional', 'سبب الإلغاء (اختياري)')}
          inputPlaceholder={t('cancel_reason_ph', 'اكتب سبباً مختصراً يصل للمرسلين...')}
          multiline
          loading={cancel.isPending}
          errorMessage={dialogError}
          confirmLabel={t('confirm_cancel_container', 'نعم، إلغاء الحاوية')}
          destructive
          onCancel={() => setDialog(null)}
          onConfirm={async (reason) => {
            try {
              await cancel.mutateAsync({ id: containerId!, reason: reason.trim() || undefined });
              setDialog(null);
            } catch (e: any) { setDialogError(e?.message || 'فشل الإلغاء'); }
          }}
        />

        {/* Seal dialog (custom — needs 3 fields) */}
        {dialog?.kind === 'seal' && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDialog(null)}
          >
            <div className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-text-primary mb-1 flex items-center gap-2">
                <Anchor size={18} /> {t('seal_container_title', 'ختم الحاوية')}
              </h3>
              <p className="text-sm text-text-secondary mb-3">
                {t('seal_body', 'أدخل أرقام الختم + الحجز + بوليصة الشحن (B/L). كل الحقول إلزامية.')}
              </p>
              {dialogError && <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-3">{dialogError}</div>}
              <div className="space-y-3">
                <TextField
                  label={t('seal_number', 'رقم الختم') + ' *'}
                  value={sealForm.sealNumber}
                  onChange={(e) => setSealForm({ ...sealForm, sealNumber: e.target.value })}
                  dir="ltr" placeholder="SL-12345"
                />
                <TextField
                  label={t('booking_ref', 'مرجع الحجز') + ' *'}
                  value={sealForm.bookingRef}
                  onChange={(e) => setSealForm({ ...sealForm, bookingRef: e.target.value })}
                  dir="ltr" placeholder="BR-12345"
                />
                <TextField
                  label={t('bol_number', 'رقم بوليصة الشحن (B/L)') + ' *'}
                  value={sealForm.bolNumber}
                  onChange={(e) => setSealForm({ ...sealForm, bolNumber: e.target.value })}
                  dir="ltr" placeholder="BL-12345"
                />
              </div>
              <div className="flex gap-2 justify-end mt-4">
                <Button variant="ghost" onClick={() => setDialog(null)} type="button">
                  {t('cancel', 'إلغاء')}
                </Button>
                <Button
                  variant="primary"
                  loading={seal.isPending}
                  disabled={!sealForm.sealNumber.trim() || !sealForm.bookingRef.trim() || !sealForm.bolNumber.trim()}
                  onClick={async () => {
                    try {
                      await seal.mutateAsync({
                        id: containerId!,
                        sealNumber: sealForm.sealNumber.trim(),
                        bookingRef: sealForm.bookingRef.trim(),
                        bolNumber: sealForm.bolNumber.trim(),
                      });
                      setDialog(null);
                    } catch (e: any) {
                      setDialogError(e?.message || 'فشل الختم');
                    }
                  }}
                >
                  {t('confirm_seal', 'ختم')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function ParticipantRow({ participant, containerId }: { participant: ContainerParticipant; containerId: number }) {
  const { t } = useTranslation();
  const approve = useApproveParticipant();
  const reject = useRejectParticipant();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const sd = participantStatusDisplay(participant.status);
  const isPending = participant.status === 'join_requested';

  return (
    <div className="border border-border rounded-xl p-3">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Hash size={11} className="text-text-tertiary" />
            <span className="font-bold text-sm text-text-primary" dir="ltr">{participant.public_code}</span>
          </div>
          {participant.title && (
            <div className="text-sm text-text-primary truncate">{participant.title}</div>
          )}
          <div className="flex flex-wrap gap-2 text-xs text-text-tertiary mt-1">
            {participant.est_weight_kg != null && <span>{participant.est_weight_kg} kg</span>}
            {participant.est_cbm != null && <span>· {participant.est_cbm} m³</span>}
            {participant.declared_value_usd != null && (
              <span>· ${Number(participant.declared_value_usd).toLocaleString()}</span>
            )}
            {participant.category && <span>· {participant.category}</span>}
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-bold ${sd.color}`}>{sd.label}</span>
      </div>
      {isPending && (
        <div className="flex gap-2 pt-2 border-t border-border/50">
          <Button variant="primary" loading={approve.isPending}
            onClick={() => approve.mutate({ containerId, pid: participant.id })}>
            <CheckCircle2 size={14} className="ml-1" /> {t('approve', 'قبول')}
          </Button>
          <Button variant="ghost"
            onClick={() => { setRejectError(null); setShowRejectDialog(true); }}>
            <XCircle size={14} className="ml-1" /> {t('reject', 'رفض')}
          </Button>
        </div>
      )}
      <PromptDialog
        open={showRejectDialog}
        title={t('reject_participant_title', 'رفض المشارك')}
        body={t('reject_participant_body', 'سيعرف المرسل سبب الرفض. اكتب سبباً واضحاً ومختصراً.')}
        inputLabel={t('reject_reason_label', 'سبب الرفض')}
        inputPlaceholder={t('reject_reason_ph', 'مثال: الوزن المُصرَّح يتجاوز السعة المتبقية...')}
        multiline required
        loading={reject.isPending}
        errorMessage={rejectError}
        confirmLabel={t('confirm_reject', 'رفض المشارك')}
        destructive
        onCancel={() => setShowRejectDialog(false)}
        onConfirm={async (reason) => {
          try {
            await reject.mutateAsync({ containerId, pid: participant.id, reason: reason.trim() });
            setShowRejectDialog(false);
          } catch (e: any) {
            setRejectError(e?.message || 'فشل الرفض');
          }
        }}
      />
    </div>
  );
}

function MilestoneAction({
  pick, setPick, loading, onAdd,
}: {
  pick: MilestoneType;
  setPick: (v: MilestoneType) => void;
  loading: boolean;
  onAdd: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 items-end w-full md:w-auto">
      <label className="block">
        <span className="text-xs text-text-secondary mb-1 block">{t('milestone', 'حدث جديد')}</span>
        <select
          value={pick}
          onChange={(e) => setPick(e.target.value as MilestoneType)}
          className={FIELD_CLASSES}
        >
          {MILESTONE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </label>
      <Button variant="primary" loading={loading} onClick={onAdd}>
        <Plus size={14} className="ml-1" /> {t('add_milestone', 'إضافة')}
      </Button>
    </div>
  );
}
